import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { de, isValidEmail, isValidLoginCode, LOGIN_CODE_RESEND_SECONDS, normalizeEmail, roleLabel } from '@fsv/core';
import { LoginError, requestCode, verifyCode } from '../auth/loginApi';
import { clearInvitePrefill, peekInvitePrefill } from '../auth/invitePrefill';
import { DEMO_ROLES, signedInWithApi, startDemo, type DemoRole } from '../auth/sessionStore';
import { demoHomePath } from '../lib/demoEntry';
import { Segmented } from '../components/Controls';
import { Footer } from '../components/Footer';
import { PrivacyText } from './PrivacyScreen';
import { Impressum, TermsOfUse } from '../legal/LegalText';
import { Icon } from '../components/Icon';
import { tx } from '@fsv/data';
import './LoginScreen.css';

const RESEND_SECONDS = LOGIN_CODE_RESEND_SECONDS;

function describe(error: unknown): string {
  if (error instanceof LoginError) {
    if (error.status === 429 && error.retryAfterMs) return de.auth.rateLimited(Math.max(1, Math.ceil(error.retryAfterMs / 60_000)));
    return error.message;
  }
  return de.errors.generic;
}

function EmailLogin({ onDone }: { onDone: () => void }) {
  const id = useId();
  // The invitation mail's link carries the invited address (auth/invitePrefill.ts).
  const [email, setEmail] = useState(() => peekInvitePrefill());
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => clearInvitePrefill(), []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [countdown]);

  const send = async (address: string) => {
    setError(null);
    if (!isValidEmail(address)) {
      setError(tx.login.invalidEmail);
      return;
    }
    setBusy(true);
    try {
      await requestCode(normalizeEmail(address));
      setSentTo(normalizeEmail(address));
      setInfo(de.auth.codeSentGeneric);
      setCountdown(RESEND_SECONDS);
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!sentTo) return;
    setError(null);
    if (!isValidLoginCode(code)) {
      setError(tx.login.invalidCodeFormat);
      return;
    }
    setBusy(true);
    try {
      const result = await verifyCode(sentTo, code.trim());
      signedInWithApi(result.token, result.me);
      onDone();
    } catch (err) {
      setError(describe(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (!sentTo) {
    return (
      <form
        className="login__form"
        onSubmit={(e) => {
          e.preventDefault();
          void send(email);
        }}
        noValidate
      >
        <div className="field">
          <label className="field__label" htmlFor={`${id}-email`}>
            {de.auth.emailLabel}
          </label>
          <input
            id={`${id}-email`}
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={error !== null}
            aria-describedby={error ? `${id}-err` : undefined}
            required
          />
        </div>
        {error && (
          <p id={`${id}-err`} className="field__error" role="alert">
            {error}
          </p>
        )}
        <button className="btn btn--primary btn--block" disabled={busy}>
          {de.auth.requestCode}
          <Icon name="forward" />
        </button>
      </form>
    );
  }

  return (
    <form className="login__form" onSubmit={verify} noValidate>
      <div className="notice" role="status">
        <Icon name="message" />
        <div>
          <p>{info}</p>
          <p className="muted">{tx.login.codeSentTo(sentTo)}</p>
        </div>
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-code`}>
          {de.auth.codeLabel}
        </label>
        <input
          id={`${id}-code`}
          className="input input--code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          aria-describedby={`${id}-hint${error ? ` ${id}-err` : ''}`}
          aria-invalid={error !== null}
          autoFocus
        />
        <p id={`${id}-hint`} className="field__hint">
          {tx.login.codeHint}
        </p>
      </div>
      {error && (
        <p id={`${id}-err`} className="field__error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn--primary btn--block" disabled={busy || code.length !== 6}>
        {de.auth.verify}
      </button>
      <div className="row login__secondary">
        <button type="button" className="link-btn" disabled={countdown > 0 || busy} onClick={() => void send(sentTo)}>
          {countdown > 0 ? de.auth.resendIn(countdown) : de.auth.resend}
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            setSentTo(null);
            setCode('');
            setError(null);
          }}
        >
          {tx.login.otherAddress}
        </button>
      </div>
    </form>
  );
}

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [demoRole, setDemoRole] = useState<DemoRole>('editor');
  const from = (location.state as { from?: unknown } | null)?.from;
  // Back to where the session ran out, else to `/`, which picks the tenant (lib/tenantRouting).
  const done = () => navigate(typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/', { replace: true });

  return (
    <div className="login">
      <div className="login__sheet">
        <header className="login__head">
          <img className="login__mark" src="/icons/icon-192.png" alt="" width={64} height={64} />
          <div>
            <p className="label login__club">{tx.login.kicker}</p>
            <h1 className="login__title">{de.app.name}</h1>
          </div>
        </header>
        <section className="login__card" aria-labelledby="login-h">
          <h2 id="login-h" className="sr-only">
            {tx.login.title}
          </h2>
          <p className="muted">{tx.login.intro}</p>
          {/* The e-mail code is the only way in: the Vereinsflieger sign-in went on 06.10.2026 (ADR 0002). */}
          <EmailLogin onDone={done} />
        </section>
        <section className="login__demo" aria-labelledby="demo-h">
          <h2 id="demo-h" className="login__demo-title">
            {tx.login.demoTitle}
          </h2>
          <p className="field__hint">{tx.login.demoHint}</p>
          <div className="row login__demo-row">
            <span className="field__label">{tx.login.demoAs}</span>
            <Segmented label={tx.login.demoAs} value={demoRole} onChange={setDemoRole} options={DEMO_ROLES.map((r) => ({ value: r, label: roleLabel[r] }))} />
          </div>
          <button
            type="button"
            className="btn btn--ink btn--block"
            onClick={() => {
              startDemo(demoRole);
              // Always the demo club's fleet: a path from a real session would be a foreign club here.
              navigate(demoHomePath(), { replace: true });
            }}
          >
            <Icon name="eye" />
            {tx.login.demoButton}
          </button>
        </section>
        <p className="login__privacy">
          {tx.login.privacy} <Link to="/datenschutz">{tx.login.privacyLink}</Link> · <Link to="/impressum">Impressum</Link> ·{' '}
          <Link to="/nutzungsbedingungen">{tx.login.termsLink}</Link> · <Link to="/hilfe/anmelden">{tx.help.title}</Link>
        </p>
      </div>
      <Footer />
    </div>
  );
}

/** /impressum, reachable without a session. */
export function ImpressumScreen() {
  return (
    <div className="login">
      <article className="login__sheet privacy">
        <Link to="/login" className="btn btn--ghost privacy__back">
          <Icon name="back" />
          {tx.app.back}
        </Link>
        <Impressum />
      </article>
      <Footer />
    </div>
  );
}

/** /nutzungsbedingungen, reachable without a session: the operator's terms of use for members (texts.ts). */
export function TermsScreen() {
  return (
    <div className="login">
      <article className="login__sheet privacy">
        <Link to="/login" className="btn btn--ghost privacy__back">
          <Icon name="back" />
          {tx.app.back}
        </Link>
        <TermsOfUse />
      </article>
      <Footer />
    </div>
  );
}

/** /datenschutz, reachable without a session: what the app processes, and who is responsible. */
export function PrivacyScreen() {
  return (
    <div className="login">
      <article className="login__sheet privacy">
        <Link to="/login" className="btn btn--ghost privacy__back">
          <Icon name="back" />
          {tx.app.back}
        </Link>
        <PrivacyText />
      </article>
      <Footer />
    </div>
  );
}
